// =============================================================================
// DEVIS AMO - Formulaire simple pour TEF
// =============================================================================
// Remplace le wizard multi-etapes pour le tenant TEF.
// Genere 2 documents : Devis AMO + Contrat AMO
// =============================================================================

import { useState, useEffect } from "react";
import {
  FileText, Download, Loader2, ArrowLeft, User,
  CheckCircle2, MapPin, Phone, Mail, AlertCircle, Plus
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { formatClientAddress, formatClientFullName } from "../../utils/clientFormat";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = (tenantId) => {
  const token = localStorage.getItem("session_token");
  return {
    "X-Tenant-ID": tenantId || "tef",
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const formatEur = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);

// Missions standard
const MISSIONS = [
  { label: "Diagnostic d'éligibilité MaPrimeAdapt'", ht: 0, ttc: 0, offert: true },
  { label: "Ouverture de dossier", ht: 45.45, ttc: 50.0 },
  { label: "Diagnostic autonomie-logement", ht: 227.27, ttc: 250.0 },
  { label: "Analyse de devis installateurs", ht: 90.91, ttc: 100.0 },
  { label: "Montage et suivi des demandes de financements", ht: 90.91, ttc: 100.0 },
  { label: "Suivi de l'execution et clôture du projet", ht: 90.91, ttc: 100.0 },
];
const MISSION_ERGO = { label: "Ergotherapeute", ht: 181.82, ttc: 200.0 };
const AIDE_AMO_COMPLET = 600;
const AIDE_AMO_ERGO = 800;

export default function DevisAMOForm({ dossierId, tenantId = "tef", onBack }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [client, setClient] = useState(null);
  const [typePrestation, setTypePrestation] = useState("amo_complet");
  const [generated, setGenerated] = useState(null);
  const [extraLine, setExtraLine] = useState(false);
  const [extraLabel, setExtraLabel] = useState("");
  const [extraTTC, setExtraTTC] = useState("");
  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState(null);

  // Load dossier + client
  useEffect(() => {
    if (!dossierId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
          headers: getAuthHeaders(tenantId),
        });
        if (!res.ok) throw new Error("Dossier introuvable");
        const data = await res.json();
        setDossier(data);
        // Client embedded or fetch separately
        if (data.client && data.client.nom) {
          setClient(data.client);
        } else if (data.client_id) {
          const rc = await fetch(`${API}/api/metier/clients/${data.client_id}`, {
            headers: getAuthHeaders(tenantId),
          });
          if (rc.ok) setClient(await rc.json());
        }
        // Check if already generated
        const tef = (data.workflow_data || {}).tef || {};
        if (tef.devis_amo_genere) {
          setGenerated({
            devis: { download_url: `/api/metier/tef/dossiers/${dossierId}/devis-amo/download` },
            contrat: { download_url: `/api/metier/tef/dossiers/${dossierId}/contrat-amo/download` },
          });
          // Toujours defaut AMO Complet, ne pas restaurer le choix precedent
        }
      } catch (e) {
        toast.error("Erreur chargement dossier");
      } finally {
        setLoading(false);
      }
    })();
  }, [dossierId, tenantId]);

  // Charger les versions de missions AMO du tenant (sélecteur "Quelle version ?")
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/governance/tenant/missions-amo`, {
          headers: getAuthHeaders(tenantId),
        });
        if (res.ok) {
          const d = await res.json();
          setVersions(d.versions || []);
          setVersionId(d.default_id || (d.versions && d.versions[0] && d.versions[0].id) || null);
        }
      } catch { /* fallback sur les constantes locales */ }
    })();
  }, [tenantId]);

  const includeErgo = typePrestation === "amo_ergo";
  const curVer = versions.find((v) => v.id === versionId) || null;
  const baseMissions = (curVer?.missions && curVer.missions.length) ? curVer.missions : MISSIONS;
  const ergoMission = curVer?.mission_ergo || MISSION_ERGO;
  const missions = includeErgo ? [...baseMissions, ergoMission] : baseMissions;
  const extraTTCNum = extraLine && extraTTC ? parseFloat(extraTTC) || 0 : 0;
  const extraHTNum = Math.round((extraTTCNum / 1.10) * 100) / 100;
  const sousTotalHT = missions.reduce((s, m) => s + (Number(m.ht) || 0), 0) + extraHTNum;
  const sousTotalTTC = missions.reduce((s, m) => s + (Number(m.ttc) || 0), 0) + extraTTCNum;
  const aideDeduction = includeErgo
    ? (curVer?.aide_deduction_ergo ?? AIDE_AMO_ERGO)
    : (curVer?.aide_deduction ?? AIDE_AMO_COMPLET);
  const resteACharge = Math.max(0, sousTotalTTC - aideDeduction);
  const hasContact = !!(client?.email || client?.telephone);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(
        `${API}/api/metier/tef/dossiers/${dossierId}/devis-amo/generer`,
        {
          method: "POST",
          headers: getAuthHeaders(tenantId),
          body: JSON.stringify({
            include_ergo: includeErgo,
            missions_amo_version: versionId,
            client_overrides: { email: client?.email, telephone: client?.telephone },
            extra_line: extraLine ? { label: extraLabel, ttc: extraTTCNum } : null,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur generation");
      }
      const result = await res.json();
      setGenerated(result);
      toast.success("Documents generes avec succes !");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (url, filename) => {
    const fullUrl = `${API}${url}`;
    fetch(fullUrl, { headers: getAuthHeaders(tenantId) })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Erreur telechargement"));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="devis-amo-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--oh-info-text)]" />
        <span className="ml-3 text-[var(--oh-text-muted)]">Chargement du dossier...</span>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="p-6" data-testid="devis-amo-error">
        <div className="flex items-center gap-2 text-[var(--oh-error-text)] mb-4">
          <AlertCircle className="w-5 h-5" />
          <span>Dossier introuvable</span>
        </div>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="devis-amo-form">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="devis-amo-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-xl font-medium text-[var(--oh-text)]">
              Devis & Contrat AMO
            </h1>
            <p className="text-sm text-[var(--oh-text-muted)]">
              Dossier {dossier.référence || dossierId}
            </p>
          </div>
        </div>
        <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-[var(--oh-info-border)]">
          MaPrimeAdapt'
        </Badge>
      </div>

      {/* Client Info - Editable */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="devis-amo-client-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[var(--oh-text)] flex items-center gap-2">
            <User className="w-4 h-4 text-[var(--oh-info-text)]" /> Beneficiaire
          </CardTitle>
        </CardHeader>
        <CardContent>
          {client ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-[var(--oh-text-muted)] text-xs mb-1 block">Nom</label>
                <div className="flex items-center gap-2 text-[var(--oh-text)] font-medium">
                  <User className="w-4 h-4 text-[var(--oh-text-secondary)] shrink-0" />
                  {formatClientFullName(client)}
                </div>
              </div>
              <div>
                <label className="text-[var(--oh-text-muted)] text-xs mb-1 block">Email</label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[var(--oh-text-secondary)] shrink-0" />
                  <Input
                    value={client.email || ""}
                    onChange={(e) => setClient({ ...client, email: e.target.value })}
                    placeholder="email@exemple.fr"
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] h-8 text-sm"
                    data-testid="devis-amo-client-email"
                  />
                </div>
              </div>
              <div>
                <label className="text-[var(--oh-text-muted)] text-xs mb-1 block">Adresse</label>
                <div className="flex items-center gap-2 text-[var(--oh-text-muted)]">
                  <MapPin className="w-4 h-4 text-[var(--oh-text-secondary)] shrink-0" />
                  {formatClientAddress(client) || "-"}
                </div>
              </div>
              <div>
                <label className="text-[var(--oh-text-muted)] text-xs mb-1 block">Telephone</label>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[var(--oh-text-secondary)] shrink-0" />
                  <Input
                    value={client.telephone || ""}
                    onChange={(e) => setClient({ ...client, telephone: e.target.value })}
                    placeholder="+33..."
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] h-8 text-sm"
                    data-testid="devis-amo-client-phone"
                  />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-[var(--oh-text-muted)] text-sm">Client non renseigne dans le dossier</p>
          )}
        </CardContent>
      </Card>

      {/* Type de prestation */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="devis-amo-prestation-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[var(--oh-text)]">Type de prestation</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={typePrestation}
            onValueChange={(v) => { setTypePrestation(v); setGenerated(null); }}
          >
            <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] w-64" data-testid="devis-amo-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="amo_complet">AMO Complet (600 TTC)</SelectItem>
              <SelectItem value="amo_ergo">AMO + Ergotherapeute (800 TTC)</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Version des missions (Contrat AMO) */}
      {versions.length > 0 && (
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="devis-amo-version-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[var(--oh-text)]">Version des missions</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={versionId || ""} onValueChange={(v) => { setVersionId(v); setGenerated(null); }}>
              <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] w-80" data-testid="devis-amo-version-select">
                <SelectValue placeholder="Choisir une version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}{v.is_default ? " (par défaut)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[var(--oh-text-muted)] mt-2">
              Ancien client (devis signé papier) → choisir la version correspondante. La version utilisée est tracée sur le dossier.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Missions Table */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="devis-amo-missions-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[var(--oh-text)] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[var(--oh-info-text)]" /> Missions AMO
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto oh-responsive-table">
            <table className="w-full text-sm" data-testid="devis-amo-missions-table">
              <thead>
                <tr className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)]">
                  <th className="text-left p-3 font-medium">MISSIONS</th>
                  <th className="text-right p-3 font-medium w-56">PRIX UNITAIRES TTC</th>
                </tr>
              </thead>
              <tbody>
                {missions.map((m, i) => (
                  <tr
                    key={i}
                    className={i % 2 === 0 ? "bg-[var(--oh-bg-elevated)]" : "bg-[var(--oh-bg-elevated)]"}
                  >
                    <td className="p-3 text-[var(--oh-text-secondary)]">{m.label}</td>
                    <td className="p-3 text-right text-[var(--oh-text-secondary)] font-mono">
                      {m.offert ? (
                        <span className="text-[var(--oh-success-text)] font-medium">Offert</span>
                      ) : (
                        <span>{Number(m.ht).toFixed(2)} HT / {Number(m.ttc).toFixed(2)} TTC</span>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Ligne supplementaire optionnelle */}
                <tr className="bg-[var(--oh-bg-elevated)] border-t border-dashed border-[var(--oh-border)]">
                  <td colSpan={2} className="p-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={extraLine}
                        onCheckedChange={(v) => { setExtraLine(!!v); setGenerated(null); }}
                        data-testid="devis-amo-extra-check"
                      />
                      <span className="text-[var(--oh-text-muted)] text-sm">Ajouter une prestation supplementaire</span>
                    </div>
                  </td>
                </tr>
                {extraLine && (
                  <tr className="bg-[var(--oh-info-bg)]" data-testid="devis-amo-extra-line">
                    <td className="p-3">
                      <Input
                        value={extraLabel}
                        onChange={(e) => { setExtraLabel(e.target.value); setGenerated(null); }}
                        placeholder="Libelle de la prestation..."
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] h-8 text-sm"
                        data-testid="devis-amo-extra-label"
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 justify-end">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={extraTTC}
                          onChange={(e) => { setExtraTTC(e.target.value); setGenerated(null); }}
                          placeholder="TTC"
                          className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] h-8 text-sm w-24 text-right"
                          data-testid="devis-amo-extra-ttc"
                        />
                        <span className="text-[var(--oh-text-muted)] text-xs whitespace-nowrap">TTC</span>
                        {extraTTCNum > 0 && (
                          <span className="text-[var(--oh-text-secondary)] text-xs whitespace-nowrap">({extraHTNum.toFixed(2)} HT)</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-[var(--oh-bg-elevated)] border-t border-[var(--oh-border)]">
                  <td className="p-3 font-medium text-[var(--oh-text)]">Sous-Total</td>
                  <td className="p-3 text-right font-medium text-[var(--oh-text)] font-mono">
                    {sousTotalHT.toFixed(2)} HT / {sousTotalTTC.toFixed(2)} TTC
                  </td>
                </tr>
                <tr className="bg-[var(--oh-error-bg)] border-t border-[var(--oh-border)]">
                  <td className="p-3 font-medium text-[var(--oh-error-text)]">
                    Aides venant en deduction**
                  </td>
                  <td className="p-3 text-right font-medium text-[var(--oh-error-text)] font-mono">
                    - {formatEur(aideDeduction)}
                  </td>
                </tr>
                <tr className="bg-[var(--oh-success-bg)] border-t border-[var(--oh-border)]">
                  <td className="p-3 font-medium text-[var(--oh-success-text)]">
                    Total previsionnel a votre charge
                  </td>
                  <td className="p-3 text-right font-medium text-[var(--oh-success-text)] font-mono text-lg">
                    {formatEur(resteACharge)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <Separator className="border-[var(--oh-border)]" />

      {/* Actions */}
      {!hasContact && client && (
        <div className="flex items-center gap-2 text-[var(--oh-info-text)] text-sm bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)] rounded-md px-4 py-2" data-testid="devis-amo-contact-warning">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Veuillez renseigner au moins un email ou un telephone pour générer les documents.
        </div>
      )}
      <div className="flex items-center justify-between" data-testid="devis-amo-actions">
        {!generated ? (
          <Button
            onClick={handleGenerate}
            disabled={generating || !client || !hasContact}
            className="bg-orange-600 hover:bg-orange-700 text-[var(--oh-accent-contrast)] px-8 py-3 text-base"
            data-testid="devis-amo-generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generation en cours...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Générer Devis + Contrat AMO
              </>
            )}
          </Button>
        ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-[var(--oh-success-text)]">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Documents generes</span>
            </div>
            <Button
              variant="outline"
              onClick={() => handleDownload(generated.devis.download_url, `Devis_AMO_${dossier.référence || dossierId}.pdf`)}
              className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
              data-testid="devis-amo-download-devis-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Telecharger Devis AMO
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownload(generated.contrat.download_url, `Contrat_AMO_${dossier.référence || dossierId}.pdf`)}
              className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
              data-testid="devis-amo-download-contrat-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Telecharger Contrat AMO
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setGenerated(null)}
              className="text-[var(--oh-text-muted)]"
              data-testid="devis-amo-regenerate-btn"
            >
              Regenerer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
